import { redirect } from '@tanstack/react-router';
import { getSubmissionData } from '../api/submissions/get-by-public-id';
import {
  UserType, SurveySettings, Submission,
} from '../types';
import {
  ApiError, isNotFoundError, isUnauthorizedError, NotFoundErrorCode,
} from './errors';
import { getSettingsByUrlSlug } from '../api/settings/get-by-url-slug';

export interface SubmissionSearchParams {
  token?: string;
}

interface SubmissionLoaderParams {
  params: { accountSlug: string; submissionId: string; token?: string };
  userType: UserType;
  deps: SubmissionSearchParams;
  redirectPath?: (accountSlug: string, submissionId: string) => string;
}

export interface SubmissionLoaderResponse {
  settings: SurveySettings;
  submission?: Submission;
  error?: ApiError;
}

async function submissionLoader ({
  params: {
    accountSlug,
    submissionId,
    token: routeToken,
  },
  userType,
  deps: { token: searchToken },
  redirectPath,
}: SubmissionLoaderParams): Promise<SubmissionLoaderResponse> {
  try {
    // Use token from route params (new format) or search params (backward compat)
    const effectiveToken = routeToken || searchToken;

    const { settings, submission } = await getSubmissionData(accountSlug, submissionId, userType, effectiveToken);

    // Remove token from URL after successful verification to clean up browser history
    // Using `replace: true` ensures the token URL doesn't remain in browser history
    if (effectiveToken) {
      // Redirect to clean URL without token
      // Default to base submission route, but allow custom redirect path (e.g., for signer route)
      const targetPath = redirectPath
        ? redirectPath(accountSlug, submissionId)
        : `/${accountSlug}/${submissionId}`;
      throw redirect({
        to: targetPath,
        replace: true,
      });
    }

    return {
      settings,
      submission,
    };
  } catch (error) {
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/' });
    }

    if (isUnauthorizedError(error) || (isNotFoundError(error) && error.code === NotFoundErrorCode.SubmissionNotFound)) {
      const settings = await getSettingsByUrlSlug(accountSlug);
      return {
        settings,
        error,
      };
    }

    throw error;
  }
}

/**
 * Returns route configuration options for submission routes
 * Can be spread directly in the route configuration
 */
export function submissionRouteLoader (userType: UserType, redirectPath?: (accountSlug: string, submissionId: string) => string) {
  return {
    // Extract loader dependencies - fallback to search for backward compat (delete after this has been deployed for a while)
    loaderDeps: ({ search }: { search: SubmissionSearchParams }): SubmissionSearchParams => ({
      token: search.token,
    }),

    // Load data using the validated params
    loader: async ({
      params, deps,
    }: { params: { accountSlug: string; submissionId: string; token?: string }, deps: SubmissionSearchParams }) => {
      return submissionLoader({
        params,
        deps,
        userType,
        redirectPath,
      });
    },
  };
}

/**
 * Helper function to construct a submission URL with token path param
 */
export function buildSubmissionUrl (accountSlug: string, submissionId: string, token?: string): string {
  const basePath = `/${accountSlug}/${submissionId}`;
  if (!token) {
    return basePath;
  }
  return `${basePath}/${token}`;
}
